import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  OwnerProfile, Horse, PublicHorseSummary, MedicalRecord, Reminder,
  DocumentItem, NewsPost, UsageInfo, BillingInfo, HorseVisibility,
  Farm, FarmInvite, FarmRosterEntry, FarmHorseView, MyYard, FarmShareCategory,
  Ticket, TicketStatus, FarmRole, FarmMember, YardTask, Invoice, InvoiceLine,
  LiveryPackage, HorseShare, HorseShareInvite, HorseShareRole,
  AdminOverview, AdminYardRow, AdminUserRow, AdminUserDetail,
} from '@horsehub/shared'
import { apiFetch, publicFetch } from './client'

// ── Queries ───────────────────────────────────────────────────────────────
export const useMe = (enabled = true) =>
  useQuery({ queryKey: ['me'], queryFn: () => apiFetch<OwnerProfile>('/api/me'), enabled })

export const useUsage = () =>
  useQuery({ queryKey: ['usage'], queryFn: () => apiFetch<UsageInfo>('/api/me/usage') })

export const useBilling = () =>
  useQuery({ queryKey: ['billing'], queryFn: () => apiFetch<BillingInfo>('/api/me/billing') })

export const useHorses = (enabled = true) =>
  useQuery({
    queryKey: ['horses'],
    queryFn: () => apiFetch<{ horses: Horse[] }>('/api/horses').then((r) => r.horses),
    enabled,
  })

export const useHorse = (id: string) =>
  useQuery({ queryKey: ['horse', id], queryFn: () => apiFetch<Horse>(`/api/horses/${id}`), enabled: !!id })

export const useMedical = (horseId: string) =>
  useQuery({
    queryKey: ['medical', horseId],
    queryFn: () => apiFetch<{ records: MedicalRecord[] }>(`/api/horses/${horseId}/medical`).then((r) => r.records),
    enabled: !!horseId,
  })

export const useReminders = (horseId: string) =>
  useQuery({
    queryKey: ['reminders', horseId],
    queryFn: () => apiFetch<{ reminders: Reminder[] }>(`/api/horses/${horseId}/reminders`).then((r) => r.reminders),
    enabled: !!horseId,
  })

export const useDocuments = (horseId: string) =>
  useQuery({
    queryKey: ['documents', horseId],
    queryFn: () => apiFetch<{ documents: DocumentItem[] }>(`/api/horses/${horseId}/documents`).then((r) => r.documents),
    enabled: !!horseId,
  })

export const useNews = (horseId: string) =>
  useQuery({
    queryKey: ['news', horseId],
    queryFn: () => apiFetch<{ news: NewsPost[] }>(`/api/horses/${horseId}/news`).then((r) => r.news),
    enabled: !!horseId,
  })

export const useFollowing = (enabled = true) =>
  useQuery({
    queryKey: ['following'],
    queryFn: () => apiFetch<{ horses: PublicHorseSummary[] }>('/api/me/following').then((r) => r.horses),
    enabled,
  })

export interface FeedEntry extends NewsPost {
  horseName: string
  horseProfilePhotoUrl?: string
}
export const useFeed = (enabled = true) =>
  useQuery({
    queryKey: ['feed'],
    queryFn: () => apiFetch<{ feed: FeedEntry[] }>('/api/feed').then((r) => r.feed),
    enabled,
  })

export interface DiscoverFilters {
  country?: string
  breed?: string
  discipline?: string
  gender?: string
}
export const useDiscover = (filters: DiscoverFilters = {}) =>
  useQuery({
    queryKey: ['discover', filters],
    queryFn: () => {
      const qs = new URLSearchParams(Object.entries(filters).filter(([, v]) => v) as [string, string][])
      return publicFetch<{ horses: PublicHorseSummary[] }>(`/api/public/horses?${qs}`).then((r) => r.horses)
    },
  })

export const usePublicHorse = (id: string) =>
  useQuery({ queryKey: ['public-horse', id], queryFn: () => publicFetch<Horse>(`/api/public/horses/${id}`), enabled: !!id, retry: false })

export const usePublicNews = (id: string) =>
  useQuery({
    queryKey: ['public-news', id],
    queryFn: () => publicFetch<{ news: NewsPost[] }>(`/api/public/horses/${id}/news`).then((r) => r.news),
    enabled: !!id,
  })

// ── Mutations ─────────────────────────────────────────────────────────────
export function useUpdateMe() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Partial<OwnerProfile> & { avatarKey?: string }) =>
      apiFetch<OwnerProfile>('/api/me', { method: 'PUT', body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  })
}

export function useMockSubscribe() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => apiFetch<BillingInfo>('/api/billing/mock-subscribe', { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['billing'] })
      qc.invalidateQueries({ queryKey: ['me'] })
    },
  })
}

/** Stripe checkout (horse-owner premium). { url } → redirect; { mock } → mock flow. */
export interface CheckoutResult {
  url?: string
  mock?: boolean
}
export const startCheckout = () => apiFetch<CheckoutResult>('/api/billing/checkout', { method: 'POST' })
export const startFarmCheckout = (farmId: string) =>
  apiFetch<CheckoutResult>(`/api/farms/${farmId}/checkout`, { method: 'POST' })

export function useCreateHorse() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Partial<Horse>) => apiFetch<Horse>('/api/horses', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['horses'] })
      qc.invalidateQueries({ queryKey: ['me'] })
    },
  })
}

export function useUpdateHorse(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Partial<Horse>) => apiFetch<Horse>(`/api/horses/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    onSuccess: () => invalidateHorse(qc, id),
  })
}

export function useDeleteHorse() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/horses/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['horses'] })
      qc.invalidateQueries({ queryKey: ['me'] })
    },
  })
}

export function useSetVisibility(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { public?: boolean; visibility?: Partial<HorseVisibility> }) =>
      apiFetch<Horse>(`/api/horses/${id}/visibility`, { method: 'PUT', body: JSON.stringify(body) }),
    onSuccess: () => invalidateHorse(qc, id),
  })
}

export function useAddMedical(horseId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Partial<MedicalRecord> & { attachmentKeys?: string[] }) =>
      apiFetch<MedicalRecord>(`/api/horses/${horseId}/medical`, { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['medical', horseId] })
      qc.invalidateQueries({ queryKey: ['usage'] })
    },
  })
}
export function useDeleteMedical(horseId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (rid: string) => apiFetch(`/api/horses/${horseId}/medical/${rid}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['medical', horseId] }),
  })
}

export function useAddReminder(horseId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Partial<Reminder>) =>
      apiFetch<Reminder>(`/api/horses/${horseId}/reminders`, { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reminders', horseId] }),
  })
}
export function useUpdateReminder(horseId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ rid, body }: { rid: string; body: Record<string, unknown> }) =>
      apiFetch<Reminder>(`/api/horses/${horseId}/reminders/${rid}`, { method: 'PUT', body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reminders', horseId] }),
  })
}
export function useDeleteReminder(horseId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (rid: string) => apiFetch(`/api/horses/${horseId}/reminders/${rid}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reminders', horseId] }),
  })
}

export function useAddDocument(horseId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { key: string; name: string; kind?: string; expiresAt?: string }) =>
      apiFetch<DocumentItem>(`/api/horses/${horseId}/documents`, { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['documents', horseId] })
      qc.invalidateQueries({ queryKey: ['usage'] })
    },
  })
}
export function useDeleteDocument(horseId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (docId: string) => apiFetch(`/api/horses/${horseId}/documents/${docId}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['documents', horseId] })
      qc.invalidateQueries({ queryKey: ['usage'] })
    },
  })
}

export function useAddNews(horseId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { title: string; body: string; imageKeys?: string[] }) =>
      apiFetch<NewsPost>(`/api/horses/${horseId}/news`, { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['news', horseId] })
      qc.invalidateQueries({ queryKey: ['usage'] })
    },
  })
}
export function useDeleteNews(horseId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (nid: string) => apiFetch(`/api/horses/${horseId}/news/${nid}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['news', horseId] })
      qc.invalidateQueries({ queryKey: ['usage'] })
    },
  })
}

export function useAddPhoto(horseId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (key: string) => apiFetch<Horse>(`/api/horses/${horseId}/photos`, { method: 'POST', body: JSON.stringify({ key }) }),
    onSuccess: () => {
      invalidateHorse(qc, horseId)
      qc.invalidateQueries({ queryKey: ['usage'] })
    },
  })
}
export function useRemovePhoto(horseId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (key: string) => apiFetch<Horse>(`/api/horses/${horseId}/photos`, { method: 'DELETE', body: JSON.stringify({ key }) }),
    onSuccess: () => {
      invalidateHorse(qc, horseId)
      qc.invalidateQueries({ queryKey: ['usage'] })
    },
  })
}

export function useFollow() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, following }: { id: string; following: boolean }) =>
      apiFetch(`/api/horses/${id}/follow`, { method: following ? 'DELETE' : 'POST' }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['public-horse', vars.id] })
      qc.invalidateQueries({ queryKey: ['following'] })
      qc.invalidateQueries({ queryKey: ['feed'] })
    },
  })
}

function invalidateHorse(qc: ReturnType<typeof useQueryClient>, id: string) {
  qc.invalidateQueries({ queryKey: ['horse', id] })
  qc.invalidateQueries({ queryKey: ['horses'] })
}

// ── Farm (B2B) ──────────────────────────────────────────────────────────────
export interface YardReminder extends Reminder {
  horseName: string
  ownerName: string
}

/** The farm I own (farm-owner side), or null. */
export const useMyFarm = (enabled = true) =>
  useQuery({
    queryKey: ['my-farm'],
    queryFn: () => apiFetch<{ farm: Farm | null }>('/api/farms/mine').then((r) => r.farm),
    enabled,
  })

export const useFarmBilling = (farmId?: string) =>
  useQuery({
    queryKey: ['farm-billing', farmId],
    queryFn: () => apiFetch<BillingInfo>(`/api/farms/${farmId}/billing`),
    enabled: !!farmId,
  })

export const useRoster = (farmId?: string) =>
  useQuery({
    queryKey: ['roster', farmId],
    queryFn: () => apiFetch<{ roster: FarmRosterEntry[] }>(`/api/farms/${farmId}/roster`).then((r) => r.roster),
    enabled: !!farmId,
  })

export const useFarmHorse = (farmId?: string, horseId?: string) =>
  useQuery({
    queryKey: ['farm-horse', farmId, horseId],
    queryFn: () => apiFetch<FarmHorseView>(`/api/farms/${farmId}/horses/${horseId}`),
    enabled: !!farmId && !!horseId,
  })

export const useYardReminders = (farmId?: string) =>
  useQuery({
    queryKey: ['yard-reminders', farmId],
    queryFn: () => apiFetch<{ reminders: YardReminder[] }>(`/api/farms/${farmId}/reminders`).then((r) => r.reminders),
    enabled: !!farmId,
  })

export const useInvites = (farmId?: string) =>
  useQuery({
    queryKey: ['invites', farmId],
    queryFn: () => apiFetch<{ invites: FarmInvite[] }>(`/api/farms/${farmId}/invites`).then((r) => r.invites),
    enabled: !!farmId,
  })

/** Pending invites addressed to me (horse-owner side). */
export const useMyInvites = (enabled = true) =>
  useQuery({
    queryKey: ['my-invites'],
    queryFn: () => apiFetch<{ invites: FarmInvite[] }>('/api/me/invites').then((r) => r.invites),
    enabled,
  })

/** Every yard I've joined as a boarder (horse-owner side). */
export const useMyYards = (enabled = true) =>
  useQuery({
    queryKey: ['my-yards'],
    queryFn: () => apiFetch<{ yards: MyYard[] }>('/api/me/yards').then((r) => r.yards),
    enabled,
  })

export function useCreateFarm() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      name: string; about?: string; country?: string; city?: string; avatarKey?: string
      addressLine?: string; postcode?: string; phone?: string
    }) => apiFetch<Farm>('/api/farms', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-farm'] })
      qc.invalidateQueries({ queryKey: ['me'] })
    },
  })
}

export function useUpdateFarm(farmId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch<Farm>(`/api/farms/${farmId}`, { method: 'PUT', body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-farm'] }),
  })
}

export function useFarmSubscribe(farmId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => apiFetch<BillingInfo>(`/api/farms/${farmId}/mock-subscribe`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['farm-billing', farmId] })
      qc.invalidateQueries({ queryKey: ['my-farm'] })
    },
  })
}

export function useCreateInvite(farmId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { email: string; invitedName?: string; role?: FarmRole }) =>
      apiFetch<FarmInvite>(`/api/farms/${farmId}/invites`, { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invites', farmId] }),
  })
}

export function useRevokeInvite(farmId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (token: string) => apiFetch(`/api/farms/${farmId}/invites/${token}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invites', farmId] }),
  })
}

export function useAcceptInvite() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (token: string) => apiFetch<{ farm: Farm }>(`/api/invite/${token}/accept`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-yards'] })
      qc.invalidateQueries({ queryKey: ['my-invites'] })
      qc.invalidateQueries({ queryKey: ['me'] })
    },
  })
}

export function useBoardHorse() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ horseId, farmId, liveryPackage, stableLocation }: { horseId: string; farmId: string; liveryPackage?: LiveryPackage; stableLocation?: string }) =>
      apiFetch<Horse>(`/api/horses/${horseId}/board`, { method: 'POST', body: JSON.stringify({ farmId, liveryPackage, stableLocation }) }),
    onSuccess: (_d, v) => {
      invalidateHorse(qc, v.horseId)
      qc.invalidateQueries({ queryKey: ['roster'] })
    },
  })
}

export function useUnboardHorse() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (horseId: string) => apiFetch<Horse>(`/api/horses/${horseId}/unboard`, { method: 'POST' }),
    onSuccess: (_d, horseId) => {
      invalidateHorse(qc, horseId)
      qc.invalidateQueries({ queryKey: ['roster'] })
    },
  })
}

export function useSetFarmHidden(horseId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (hidden: FarmShareCategory[]) =>
      apiFetch<Horse>(`/api/horses/${horseId}/farm-visibility`, { method: 'PUT', body: JSON.stringify({ hidden }) }),
    onSuccess: () => {
      invalidateHorse(qc, horseId)
      qc.invalidateQueries({ queryKey: ['roster'] })
    },
  })
}

/**
 * The yard the caller WORKS at (as owner, manager or groom) — the context for
 * the yard-side pages. Owners get their own yard; staff get the first yard
 * they hold a staff role at. Boarders get none (they use /my-farm instead).
 */
export function useWorkYard(): {
  farmId?: string
  farmName?: string
  role?: FarmRole
  isLoading: boolean
} {
  const { data: me, isLoading: meLoading } = useMe()
  const isFarmOwner = !!me?.roles?.farmOwner
  const { data: myFarm, isLoading: farmLoading } = useMyFarm(isFarmOwner)
  const { data: yards, isLoading: yardsLoading } = useMyYards()
  if (isFarmOwner && myFarm) {
    return { farmId: myFarm.id, farmName: myFarm.name, role: 'owner', isLoading: false }
  }
  const staffYard = (yards ?? []).find((y) => y.role === 'manager' || y.role === 'groom')
  if (staffYard) {
    return { farmId: staffYard.id, farmName: staffYard.name, role: staffYard.role, isLoading: false }
  }
  return { farmId: undefined, farmName: undefined, role: undefined, isLoading: meLoading || farmLoading || yardsLoading }
}

// ── Yard members (staff + livery clients) ───────────────────────────────────
export const useMembers = (farmId?: string) =>
  useQuery({
    queryKey: ['members', farmId],
    queryFn: () => apiFetch<{ members: FarmMember[] }>(`/api/farms/${farmId}/members`).then((r) => r.members),
    enabled: !!farmId,
  })

export function useUpdateMemberRole(farmId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: FarmRole }) =>
      apiFetch<FarmMember>(`/api/farms/${farmId}/members/${userId}`, { method: 'PUT', body: JSON.stringify({ role }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['members', farmId] }),
  })
}

export function useRemoveMember(farmId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) => apiFetch(`/api/farms/${farmId}/members/${userId}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['members', farmId] })
      qc.invalidateQueries({ queryKey: ['roster', farmId] })
      qc.invalidateQueries({ queryKey: ['my-farm'] })
    },
  })
}

// ── Daily yard tasks ─────────────────────────────────────────────────────────
export const useTasks = (farmId?: string, from?: string, to?: string) =>
  useQuery({
    queryKey: ['tasks', farmId, from, to],
    queryFn: () => {
      const qs = new URLSearchParams()
      if (from) qs.set('from', from)
      if (to) qs.set('to', to)
      return apiFetch<{ tasks: YardTask[] }>(`/api/farms/${farmId}/tasks?${qs}`).then((r) => r.tasks)
    },
    enabled: !!farmId,
  })

export function useCreateTask(farmId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { date: string; title: string; notes?: string; horseId?: string; assignedToId?: string }) =>
      apiFetch<YardTask>(`/api/farms/${farmId}/tasks`, { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks', farmId] }),
  })
}

export function useUpdateTask(farmId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ date, tid, body }: { date: string; tid: string; body: Record<string, unknown> }) =>
      apiFetch<YardTask>(`/api/farms/${farmId}/tasks/${date}/${tid}`, { method: 'PUT', body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks', farmId] }),
  })
}

export function useDeleteTask(farmId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ date, tid }: { date: string; tid: string }) =>
      apiFetch(`/api/farms/${farmId}/tasks/${date}/${tid}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks', farmId] }),
  })
}

export function useCopyTasks(farmId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { from: string; to: string }) =>
      apiFetch<{ copied: number }>(`/api/farms/${farmId}/tasks/copy`, { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks', farmId] }),
  })
}

// ── Livery invoices ──────────────────────────────────────────────────────────
export const useFarmInvoices = (farmId?: string) =>
  useQuery({
    queryKey: ['farm-invoices', farmId],
    queryFn: () => apiFetch<{ invoices: Invoice[] }>(`/api/farms/${farmId}/invoices`).then((r) => r.invoices),
    enabled: !!farmId,
  })

export const useMyInvoices = (enabled = true) =>
  useQuery({
    queryKey: ['my-invoices'],
    queryFn: () => apiFetch<{ invoices: Invoice[] }>('/api/me/invoices').then((r) => r.invoices),
    enabled,
  })

export function useCreateInvoice(farmId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      memberId: string; lines?: InvoiceLine[]; autoFromPackages?: boolean
      periodLabel?: string; dueDate?: string; notes?: string
    }) => apiFetch<Invoice>(`/api/farms/${farmId}/invoices`, { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['farm-invoices', farmId] }),
  })
}

export function useUpdateInvoice(farmId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ iid, body }: { iid: string; body: Record<string, unknown> }) =>
      apiFetch<Invoice>(`/api/farms/${farmId}/invoices/${iid}`, { method: 'PUT', body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['farm-invoices', farmId] }),
  })
}

export function useSetBoardingPackage(farmId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ horseId, liveryPackage }: { horseId: string; liveryPackage: LiveryPackage }) =>
      apiFetch(`/api/farms/${farmId}/horses/${horseId}/package`, { method: 'PUT', body: JSON.stringify({ liveryPackage }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['roster', farmId] }),
  })
}

// ── Tickets ─────────────────────────────────────────────────────────────────
export const useFarmTickets = (farmId?: string) =>
  useQuery({
    queryKey: ['farm-tickets', farmId],
    queryFn: () => apiFetch<{ tickets: Ticket[] }>(`/api/farms/${farmId}/tickets`).then((r) => r.tickets),
    enabled: !!farmId,
  })

export const useMyTickets = (enabled = true) =>
  useQuery({
    queryKey: ['my-tickets'],
    queryFn: () => apiFetch<{ tickets: Ticket[] }>('/api/me/tickets').then((r) => r.tickets),
    enabled,
  })

export function useCreateTicket() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ farmId, ...body }: { farmId: string; subject: string; message: string; category?: string; priority?: string; horseId?: string }) =>
      apiFetch<Ticket>(`/api/farms/${farmId}/tickets`, { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-tickets'] })
      qc.invalidateQueries({ queryKey: ['farm-tickets'] })
    },
  })
}

export function useUpdateTicket(farmId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ tid, status }: { tid: string; status: TicketStatus }) =>
      apiFetch<Ticket>(`/api/farms/${farmId}/tickets/${tid}`, { method: 'PUT', body: JSON.stringify({ status }) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['farm-tickets', farmId] })
      qc.invalidateQueries({ queryKey: ['my-tickets'] })
    },
  })
}


// ── Horse sharing (co-owners / viewers on one horse) ────────────────────────
export const useHorseShares = (horseId?: string) =>
  useQuery({
    queryKey: ['horse-shares', horseId],
    queryFn: () =>
      apiFetch<{ shares: HorseShare[]; invites: HorseShareInvite[] }>(`/api/horses/${horseId}/shares`),
    enabled: !!horseId,
  })

export function useCreateHorseShare(horseId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ email, role }: { email: string; role: HorseShareRole }) =>
      apiFetch(`/api/horses/${horseId}/shares`, { method: 'POST', body: JSON.stringify({ email, role }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['horse-shares', horseId] }),
  })
}

/** `target` is a userId for an active share, or a token for a pending invite. */
export function useRevokeHorseShare(horseId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (target: string) =>
      apiFetch(`/api/horses/${horseId}/shares/${target}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['horse-shares', horseId] }),
  })
}

export const useMyHorseShareInvites = (enabled = true) =>
  useQuery({
    queryKey: ['my-horse-shares'],
    queryFn: () => apiFetch<{ invites: HorseShareInvite[] }>('/api/me/horse-shares').then((r) => r.invites),
    enabled,
  })

export function useAcceptHorseShare() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (token: string) => apiFetch(`/api/horse-share/${token}/accept`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-horse-shares'] })
      qc.invalidateQueries({ queryKey: ['horses'] })
    },
  })
}

export function useLeaveHorseShare() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (horseId: string) => apiFetch(`/api/me/horse-shares/${horseId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['horses'] }),
  })
}


// ── Platform admin (read-only support console) ──────────────────────────────
export const useAdminOverview = (enabled = true) =>
  useQuery({ queryKey: ['admin-overview'], queryFn: () => apiFetch<AdminOverview>('/api/admin/overview'), enabled })

export const useAdminYards = (enabled = true) =>
  useQuery({
    queryKey: ['admin-yards'],
    queryFn: () => apiFetch<{ yards: AdminYardRow[] }>('/api/admin/yards').then((r) => r.yards),
    enabled,
  })

export const useAdminUsers = (enabled = true) =>
  useQuery({
    queryKey: ['admin-users'],
    queryFn: () => apiFetch<{ users: AdminUserRow[] }>('/api/admin/users').then((r) => r.users),
    enabled,
  })

export const useAdminUser = (uid?: string) =>
  useQuery({
    queryKey: ['admin-user', uid],
    queryFn: () => apiFetch<AdminUserDetail>(`/api/admin/users/${uid}`),
    enabled: !!uid,
  })

export interface AdminYardDetail {
  yard: AdminYardRow
  members: { userId: string; name: string; email: string; role: string; joinedAt: string }[]
  horses: { horseId: string; horseName: string; ownerName: string; ownerEmail: string; liveryPackage?: string; stableLocation?: string }[]
  invites: { email: string; role: string; status: string; createdAt: string }[]
}
export const useAdminYard = (fid?: string) =>
  useQuery({
    queryKey: ['admin-yard', fid],
    queryFn: () => apiFetch<AdminYardDetail>(`/api/admin/yards/${fid}`),
    enabled: !!fid,
  })
